import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Phone,
  CheckCircle2,
  MapPin,
  Award,
  Stethoscope,
} from 'lucide-react';
import { areasServed, getAreaBySlug, areaServices, areaConditions } from '@/data/areasServed';

const AreaDetailPage = () => {
  const { slug } = useParams();
  const area = getAreaBySlug(slug);

  if (!area) {
    return <Navigate to="/areas-we-serve" replace />;
  }

  const currentIndex = areasServed.findIndex((a) => a.slug === slug);
  const otherAreas = areasServed.filter((a) => a.slug !== slug).slice(0, 8);
  const prevArea = currentIndex > 0 ? areasServed[currentIndex - 1] : null;
  const nextArea =
    currentIndex < areasServed.length - 1 ? areasServed[currentIndex + 1] : null;

  return (
    <div className="min-h-screen" data-testid={`area-detail-${slug}`}>
      {/* Breadcrumb */}
      <div className="bg-muted py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm flex-wrap">
            <Link to="/" className="text-muted-foreground hover:text-primary">Home</Link>
            <span className="text-muted-foreground">/</span>
            <Link to="/areas-we-serve" className="text-muted-foreground hover:text-primary">
              Areas We Serve
            </Link>
            <span className="text-muted-foreground">/</span>
            <span className="text-foreground font-medium">{area.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-hero py-12 lg:py-20 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link
            to="/areas-we-serve"
            className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Areas
          </Link>
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-semibold">Serving {area.name}, FL</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
            Hand & Wrist Specialist in {area.name}, FL
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Melbourne Hand Center proudly serves patients in {area.name}, Florida with
            specialized care for hand, wrist, and elbow conditions. Our experienced team
            provides advanced surgical and non-surgical treatments tailored to each patient.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-10">
              {/* Services Offered */}
              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Stethoscope className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-foreground">
                      Services Offered in {area.name}
                    </h2>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {areaServices.map((service) => (
                      <li key={service} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Why Choose Us */}
              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                      <Award className="w-6 h-6 text-success" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-foreground">
                      Why Choose Us
                    </h2>
                  </div>
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    Board-certified specialists, advanced techniques, and a patient-first
                    approach make Melbourne Hand Center a trusted provider across Brevard
                    County. {area.name} patients benefit from same-day appointments,
                    on-site diagnostics, and fellowship-trained surgeons focused exclusively
                    on the hand, wrist, and elbow.
                  </p>
                </CardContent>
              </Card>

              {/* Conditions Treated */}
              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="font-display text-2xl font-bold text-foreground mb-4">
                    Conditions Treated
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    We diagnose and treat a wide range of hand and upper extremity conditions
                    for {area.name} residents, including:
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {areaConditions.map((condition) => (
                      <li key={condition} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{condition}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground mt-4 text-sm">
                    …and many more.{' '}
                    <Link to="/treatment-areas" className="text-primary font-medium hover:underline">
                      View all treatment areas
                    </Link>
                    .
                  </p>
                </CardContent>
              </Card>

              {/* CTA Block */}
              <Card className="bg-gradient-trust border-0 shadow-card">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="font-display text-2xl font-bold text-foreground mb-3">
                    Ready to Schedule Your Appointment?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Contact Melbourne Hand Center today to schedule your appointment if
                    you are in {area.name} or surrounding areas.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button variant="hero" size="lg" asChild>
                      <a
                        href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Calendar className="w-5 h-5 mr-2" />
                        Schedule Online
                      </a>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:321-500-4263">
                        <Phone className="w-5 h-5 mr-2" />
                        321.500.4263
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="bg-primary text-primary-foreground border-0 shadow-xl lg:sticky lg:top-24">
                <CardContent className="p-8">
                  <h3 className="font-display text-xl font-bold mb-4">
                    Visit Our Office
                  </h3>
                  <div className="space-y-3 mb-6 text-primary-foreground/90">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <p>2010 W. Eau Gallie Blvd</p>
                        <p>Suite 104</p>
                        <p>Melbourne, FL 32935</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Button variant="white" size="lg" className="w-full" asChild>
                      <a
                        href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Calendar className="w-5 h-5 mr-2" />
                        Schedule Now
                      </a>
                    </Button>
                    <Button variant="heroOutline" size="lg" className="w-full" asChild>
                      <a href="tel:321-500-4263">
                        <Phone className="w-5 h-5 mr-2" />
                        Call Us
                      </a>
                    </Button>
                  </div>
                  <p className="text-sm text-primary-foreground/70 mt-4 text-center">
                    Same-day appointments available
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-0 shadow-card">
                <CardContent className="p-6">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                    Other Areas We Serve
                  </h3>
                  <ul className="space-y-2">
                    {otherAreas.map((a) => (
                      <li key={a.slug}>
                        <Link
                          to={`/areas-we-serve/${a.slug}`}
                          className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2"
                        >
                          <ArrowRight className="w-3 h-3" />
                          {a.name}, FL
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/areas-we-serve"
                    className="inline-flex items-center gap-2 text-primary font-medium text-sm mt-4 hover:gap-3 transition-all"
                  >
                    View All Areas
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Prev / Next */}
          <div className="flex justify-between items-center mt-16 pt-8 border-t border-border gap-4">
            {prevArea ? (
              <Link
                to={`/areas-we-serve/${prevArea.slug}`}
                className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <div>
                  <p className="text-sm">Previous</p>
                  <p className="font-medium text-foreground">{prevArea.name}, FL</p>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {nextArea ? (
              <Link
                to={`/areas-we-serve/${nextArea.slug}`}
                className="flex items-center gap-3 text-right text-muted-foreground hover:text-primary transition-colors"
              >
                <div>
                  <p className="text-sm">Next</p>
                  <p className="font-medium text-foreground">{nextArea.name}, FL</p>
                </div>
                <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AreaDetailPage;
